import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSchoolFirestore } from './useSchoolFirestore';
import { auth } from '../firebase';
import { useSnapshotRead } from '../services/domain/useSnapshotRead';

const EMPTY = Object.freeze([]);
const CACHE_PREFIX = 'swan_confirmed_directory:';

function loadConfirmed(identity) {
  try {
    const saved = JSON.parse(localStorage.getItem(CACHE_PREFIX + identity));
    return saved?.version === 1 && saved.identity === identity && Array.isArray(saved.data)
      ? saved.data : null;
  } catch { return null; }
}

function saveConfirmed(identity, data) {
  try {
    if (data === null) localStorage.removeItem(CACHE_PREFIX + identity);
    else localStorage.setItem(CACHE_PREFIX + identity, JSON.stringify({ version: 1, identity, data }));
  } catch { /* Storage may be unavailable; the current session can still use its data. */ }
}

/** A collection read retains its source state; an empty cache is not a confirmed zero. */
export default function useCollectionSnapshot(collectionName, conditions = EMPTY, { enabled = true, identity: actorIdentity = null, retainConfirmed = false } = {}) {
  const { schoolId, subscribeSchoolCollection } = useSchoolFirestore();
  const path = schoolId && collectionName ? `schools/${schoolId}/${collectionName}` : null;
  const identity = JSON.stringify([path, conditions, auth?.currentUser?.uid, actorIdentity]);
  const conditionsRef = useRef(conditions);
  conditionsRef.current = conditions;
  const subscribe = useCallback(receive => subscribeSchoolCollection(collectionName,
    (data, metadata) => receive(data, null, metadata), conditionsRef.current, error => receive(EMPTY, error)),
  [collectionName, subscribeSchoolCollection]);
  const active = enabled && Boolean(path);
  const read = useSnapshotRead({ identity, subscribe, enabled: active });
  const confirmed = useRef({ identity: null, data: null });
  // Like the school/permission caches, a known directory is available before
  // subscriptions refresh. The full identity prevents reuse by a different
  // school, account, role or query. Only server snapshots populate this cache;
  // an initial empty Firestore cache must not invent an unassigned whole-school view.
  if (confirmed.current.identity !== identity || !retainConfirmed || !active) {
    confirmed.current = { identity, data: retainConfirmed && active ? loadConfirmed(identity) : null };
  }
  if (read.error) confirmed.current.data = null;
  else if (active && retainConfirmed && read.loaded && !read.loading && !read.fromCache) {
    confirmed.current.data = read.data;
  }
  useEffect(() => {
    if (!retainConfirmed || !active) return;
    if (read.error) saveConfirmed(identity, null);
    else if (read.loaded && !read.loading && !read.fromCache) saveConfirmed(identity, read.data);
  }, [retainConfirmed, active, identity, read.error, read.loaded, read.loading, read.fromCache, read.data]);

  const retryRead = read.retry;
  const retry = useCallback(() => {
    confirmed.current = { identity, data: null };
    if (retainConfirmed) saveConfirmed(identity, null);
    retryRead();
  }, [identity, retainConfirmed, retryRead]);
  const saved = confirmed.current.data;
  return useMemo(() => retainConfirmed
    ? { ...read, ...(saved ? { data: saved, loaded: true, loading: false,
      fromCache: read.loading || !read.loaded || read.fromCache } : {}), confirmed: Boolean(saved), retry }
    : read, [read, retainConfirmed, saved, retry]);
}
